import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { CheckCircle, Loader, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshCredits } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [plan, setPlan] = useState('');
  const pollCount = useRef(0);
  const hasPolled = useRef(false);

  useEffect(() => {
    if (!sessionId || hasPolled.current) return;
    hasPolled.current = true;

    const check = async () => {
      pollCount.current += 1;
      try {
        const res = await fetch(`${API_URL}/api/subscription/status/${sessionId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'active') {
            setStatus('success');
            setPlan(data.plan || '');
            refreshCredits();
            return;
          }
        }
        if (pollCount.current < 8) {
          setTimeout(check, 2500);
        } else {
          setStatus('pending');
        }
      } catch {
        if (pollCount.current < 8) setTimeout(check, 2500);
        else setStatus('error');
      }
    };
    check();
  }, [sessionId, refreshCredits]);

  if (!sessionId) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <p style={{ color: 'hsl(var(--muted-foreground))' }}>No session found.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/pricing')} style={{ marginTop: '1rem' }}>View plans</button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '500px', margin: '3rem auto', textAlign: 'center' }} data-testid="subscription-success-page">
      <div className="card" style={{ padding: '2.5rem' }}>
        {status === 'checking' && (
          <>
            <Loader size={48} style={{ color: 'hsl(var(--accent))', margin: '0 auto 1.5rem', animation: 'spin 1s linear infinite' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, fontFamily: 'Space Grotesk, sans-serif', marginBottom: '0.5rem' }}>Activating subscription...</h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>Please wait while we set up your plan.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={48} style={{ color: 'hsl(var(--accent))', margin: '0 auto 1.5rem' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, fontFamily: 'Space Grotesk, sans-serif', marginBottom: '0.5rem' }}>
              Welcome to {plan.charAt(0).toUpperCase() + plan.slice(1)}!
            </h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              Your subscription is active. Start building!
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/')} style={{ fontSize: '0.875rem' }} data-testid="start-building-button">
              Start Building
            </button>
          </>
        )}
        {status === 'pending' && (
          <>
            <Loader size={48} style={{ color: 'hsl(var(--warning))', margin: '0 auto 1.5rem' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>Processing...</h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Your subscription is being set up. This may take a moment.</p>
            <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ fontSize: '0.875rem' }}>Go to Build</button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle size={48} style={{ color: 'hsl(var(--destructive))', margin: '0 auto 1.5rem' }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>Something went wrong</h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Please contact support if the issue persists.</p>
            <button className="btn btn-secondary" onClick={() => navigate('/pricing')} style={{ fontSize: '0.875rem' }}>Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
